// =============================================
// GoalPicker — DUŻA BLOKUJĄCA BRAMA wyboru celu (STOP-9)
//
// Tryby:
// - "full_audit" (default po przejściu) = obecne zachowanie 1:1 (3 zakładki, 5 stepów, M1-M15)
// - "redirect"             = uproszczona ścieżka: pobierz wpisy → skanuj propozycje → apply pending
// - "read_only"            = tylko podgląd, wszystkie write buttons disabled
//
// STOP-1: picker po prostu istnieje, NIE zmienia logiki.
// STOP-2: gates per goalMode + 3 osobne deriveNextStep.
// STOP-9: przebudowa na DUŻĄ BLOKUJĄCĄ BRAMĘ — reszta panelu nie renderuje się
//   dopóki user nie zaakceptuje. Per-mapa localStorage persistance.
// =============================================

import { Eye, FileSearch, Link2, Check, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

export type GoalMode = "full_audit" | "redirect" | "read_only";

export interface GoalPickerProps {
  value: GoalMode | null;             // pending choice (null = nic nie wybrane)
  onChange: (mode: GoalMode) => void; // klik karty
  onAccept: () => void;                // klik "Akceptuj i przejdź"
  websiteName?: string;                // kontekstowa nazwa domeny w nagłówku
}

interface GoalOption {
  value: GoalMode;
  label: string;
  description: string;
  longHint: string;
  icon: React.ComponentType<{ className?: string }>;
  accent: {
    border: string;
    bg: string;
    text: string;
    iconBg: string;
  };
}

const GOAL_OPTIONS: GoalOption[] = [
  {
    value: "full_audit",
    label: "Pełny audyt i decyzje",
    description: "Cały pipeline M1-M15: pobierz wpisy, SRO, audyt jakości, matching, scope, Decision Engine, publish.",
    longHint: "Domyślne zachowanie — wszystkie 3 zakładki + 5 kroków widoczne. Wybierz gdy chcesz pełny proces decyzyjny.",
    icon: FileSearch,
    accent: {
      border: "border-cyan-500/70",
      bg: "bg-cyan-500/10",
      text: "text-cyan-700 dark:text-cyan-300",
      iconBg: "bg-cyan-500/20 text-cyan-700 dark:text-cyan-300",
    },
  },
  {
    value: "redirect",
    label: "Redirecty stare → nowe",
    description: "Uproszczona ścieżka: pobierz wpisy → skanuj podobieństwo node↔wp_post → zatwierdź pary jako pending redirect.",
    longHint: "Wybierz gdy migrujesz stare wpisy na nowy klaster. Krok 2-5 i Tab Decyzje ukryte.",
    icon: Link2,
    accent: {
      border: "border-amber-500/70",
      bg: "bg-amber-500/10",
      text: "text-amber-700 dark:text-amber-300",
      iconBg: "bg-amber-500/20 text-amber-700 dark:text-amber-300",
    },
  },
  {
    value: "read_only",
    label: "Tylko podejrzyj",
    description: "Read-only view tabel + export. Wszystkie write buttons disabled. Bezpieczne dla audytu bez ryzyka mutacji.",
    longHint: "Wybierz gdy chcesz tylko obejrzeć stan bez ryzyka kliknięcia czegoś destrukcyjnego.",
    icon: Eye,
    accent: {
      border: "border-slate-500/70",
      bg: "bg-slate-500/10",
      text: "text-slate-700 dark:text-slate-300",
      iconBg: "bg-slate-500/20 text-slate-700 dark:text-slate-300",
    },
  },
];

export function GoalPicker({ value, onChange, onAccept, websiteName }: GoalPickerProps) {
  const canAccept = value !== null;

  return (
    <div className="flex items-center justify-center min-h-[60vh] px-4 py-8">
      <div className="w-full max-w-5xl space-y-6">
        {/* Hero header */}
        <div className="text-center space-y-2">
          <p className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">
            Brama wyboru celu
          </p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
            Po co tu jesteś z istniejącymi wpisami?
          </h2>
          <p className="text-sm md:text-base text-muted-foreground max-w-2xl mx-auto">
            {websiteName ? (
              <>
                Wybierz cel pracy z wpisami z domeny <strong className="text-foreground">{websiteName}</strong>.
                Zmienisz to później bez ryzyka — wybór zapamiętany per mapa.
              </>
            ) : (
              <>Wybierz cel pracy. Zmienisz to później bez ryzyka — wybór zapamiętany per mapa.</>
            )}
          </p>
        </div>

        {/* 3 duże karty */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {GOAL_OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const isSelected = value === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => onChange(opt.value)}
                className={`text-left rounded-xl border-2 p-5 transition-all relative group ${
                  isSelected
                    ? `${opt.accent.border} ${opt.accent.bg} ${opt.accent.text} shadow-lg scale-[1.02]`
                    : "border-border bg-card hover:bg-muted/40 hover:border-border/80 text-foreground"
                }`}
                aria-pressed={isSelected}
              >
                {isSelected && (
                  <div
                    className={`absolute top-3 right-3 rounded-full w-6 h-6 flex items-center justify-center ${opt.accent.iconBg}`}
                  >
                    <Check className="h-4 w-4" />
                  </div>
                )}
                <div className="space-y-3">
                  <div
                    className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                      isSelected ? opt.accent.iconBg : "bg-muted/40 text-muted-foreground"
                    }`}
                  >
                    <Icon className="h-6 w-6" />
                  </div>
                  <div className="space-y-1.5">
                    <h3 className="text-lg font-bold leading-tight">{opt.label}</h3>
                    <p className={`text-sm leading-snug ${isSelected ? "" : "text-muted-foreground"}`}>
                      {opt.description}
                    </p>
                  </div>
                  <p className={`text-xs leading-relaxed pt-1 ${isSelected ? "opacity-90" : "text-muted-foreground/80"}`}>
                    {opt.longHint}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Accept bar */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-3 pt-4 border-t">
          <p className="text-xs text-muted-foreground italic">
            {canAccept
              ? `Wybrano: "${GOAL_OPTIONS.find((o) => o.value === value)?.label}". Klik "Akceptuj" aby przejść do panelu.`
              : "Wybierz jeden z 3 trybów powyżej."}
          </p>
          <Button
            size="lg"
            onClick={onAccept}
            disabled={!canAccept}
            className="min-w-[200px]"
          >
            Akceptuj i przejdź
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>

        {/* Footnote */}
        <p className="text-[11px] text-center text-muted-foreground/70 italic">
          Default po akceptacji "Pełny audyt" = obecne zachowanie 1:1. Nic nie wykonuje się automatycznie — wszystkie akcje są manualne.
        </p>
      </div>
    </div>
  );
}
